import { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Section, Container } from '../../ui';
import styles from './ConformiteRegulations.module.css';

export function ConformiteRegulations() {
  const { t } = useTranslation('solutions');
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const hasAnimated = useRef(false);

  const targets = t('regulatory.agec.targets', { returnObjects: true }) as {
    year: string; value: string; label: string;
  }[];
  const csrdPoints = t('regulatory.csrd.points', { returnObjects: true }) as string[];
  const ecoItems = t('regulatory.ecoIndicators.items', { returnObjects: true }) as {
    value: string; label: string;
  }[];

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true;
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <Section variant="gray">
      <Container>
        <div ref={ref} className={`${styles.wrapper} ${isVisible ? styles.visible : ''}`}>
          <div className={styles.grid}>
            <div className={`${styles.block} ${styles.agec}`}>
              <h3 className={styles.blockTitle}>{t('regulatory.agec.title')}</h3>
              <p className={styles.blockText}>{t('regulatory.agec.text')}</p>
              <div className={styles.targets}>
                {targets.map((target, i) => (
                  <div key={i} className={styles.target}>
                    <span className={styles.targetYear}>{target.year}</span>
                    <span className={styles.targetValue}>{target.value}</span>
                    <span className={styles.targetLabel}>{target.label}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className={`${styles.block} ${styles.csrd}`}>
              <h3 className={styles.blockTitle}>{t('regulatory.csrd.title')}</h3>
              <p className={styles.blockText}>{t('regulatory.csrd.text')}</p>
              <ul className={styles.csrdPoints}>
                {csrdPoints.map((point, i) => (
                  <li key={i} className={styles.csrdPoint}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={styles.csrdIcon} aria-hidden="true">
                      <circle cx="12" cy="12" r="10" />
                      <path d="m9 12 2 2 4-4" />
                    </svg>
                    {point}
                  </li>
                ))}
              </ul>
            </div>

            <div className={`${styles.block} ${styles.eco}`}>
              <h3 className={styles.blockTitle}>{t('regulatory.ecoIndicators.title')}</h3>
              <p className={styles.blockText}>{t('regulatory.ecoIndicators.text')}</p>
              <div className={styles.ecoItems}>
                {ecoItems.map((item, i) => (
                  <div key={i} className={styles.ecoItem}>
                    <span className={styles.ecoValue}>{item.value}</span>
                    <span className={styles.ecoLabel}>{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </Container>
    </Section>
  );
}
